import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {templateCategoriesEntitiesMapType} from '../../constants/proptype-validation';
import i18n from '../../utils/i18n';
import lodashGet from 'lodash/get';
import lodashSet from 'lodash/set';
import lodashIsEqual from 'lodash/isEqual';
import FormField from '../common/FormField';
import TemplateCategoryCreatePopupContainer from './TemplateCategoryCreatePopupContainer';
import Button from '@adsk/bim360-matrix-react-button';
import {StatusColorIndicatorOption} from '@adsk/bim360-matrix-react-status-color-indicator';
import TextFieldCore from '@adsk/bim360-matrix-react-text-field-core';
import {Behaviors as TextInputBehaviors} from '@adsk/bim360-matrix-react-text-input';
import Toggle from '@adsk/bim360-matrix-react-toggle';
import {WithScrollableContainerRef, isNullOrUndefined} from '@adsk/bim360-matrix-react-helpers';
import AnimateHeightPopUp from '../common/AnimateHeightPopUp';
import {CREATE_FORM_SEPARATOR, CREATE_TEMPLATE_FORM_FIELD_NAMES_ORDER} from '../../constants/create-form';
import {issueTemplatesStatuses, statusColorMapping} from '../../constants/status-types';
import {
  getIssueCustomAttributeInputValidationMessage,
  getTemplateInputValidationMessage,
  presenceValidator
} from '../../utils/validation-helpers';
import {TEMPLATE_FIELD_NAMES, TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES} from '../../constants/issue-templates';
import {getUserCompanyName} from '../../utils/userHelpers';
import {safeParseInteger, safeParseNumber} from '../../utils/form-helpers';
import IssueCustomAttributeField from '../forms/IssueCustomAttributeField';
import {ISSUE_ATTRIBUTES_DATA_TYPES} from '../../constants/issue-attributes';
import lodashMemoize from 'lodash/memoize';
import IssueLocationPicker from '../issues/IssueLocationPickerContainer';
import TemplateIssueTypeSelectDropdownContainer from './TemplateIssueTypeSelectDropdownContainer';
import {getTemplateValidInitialFieldValues} from '../../utils/issue-template-helpers';
import TemplateIssueRootCauseSelectDropdownContainer from './TemplateIssueRootCauseSelectDropdownContainer';
import TemplateCategorySelectBoxContainer from './TemplateCategorySelectBoxContainer';
import SelectBoxAdapter from '../common/SelectBoxAdapter';

const scrollableContainerClassName = 'TemplateEditForm__form-content';
const TemplateIssueTypeSelectDropdownScrollableContainer = WithScrollableContainerRef(
  TemplateIssueTypeSelectDropdownContainer,
  scrollableContainerClassName
);
const TemplateIssueRootCauseSelectDropdownWithScrollableContainer = WithScrollableContainerRef(
  TemplateIssueRootCauseSelectDropdownContainer,
  scrollableContainerClassName
);

class TemplateEditForm extends PureComponent {
  constructor(...args) {
    super(...args);

    this.state = {
      isNewCategoryDropdown: false,
      customAttributeMappings: []
    };

    this.dropdownMaxWidth = 250;
    this.formFieldNamesOrder = CREATE_TEMPLATE_FORM_FIELD_NAMES_ORDER;
    this.fieldComponentRefs = {};

    this.scrollableElementRef = React.createRef();

    this.statusOptions = issueTemplatesStatuses.map(status => ({
      value: status,
      label: i18n.t(`status.${status}`),
      status
    }));

    this.mandatoryFields = [
      TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.TITLE,
      TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.SUBTYPE_ID,
      TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.STATUS
    ];
    this.requiredFieldMessage = i18n.t('new.fieldRequired');
  }

  componentDidMount() {
    this.initFormValues();
  }

  componentDidUpdate(prevProps) {
    if (!lodashIsEqual(prevProps.initialValues, this.props.initialValues)) {
      this.initFormValues();
    }
  }

  getRequiredCustomAttributeIds = lodashMemoize(customAttributeMappings =>
    customAttributeMappings.filter(mapping => mapping.isRequired).map(mapping => mapping.attributeDefinitionId)
  );

  getSubTypeCustomAttributeMappings(subTypeId) {
    const {issueCustomAttributesMappings, fieldIssueTypeEntitiesMap} = this.props;
    const currentSubType = fieldIssueTypeEntitiesMap[subTypeId];
    if (currentSubType) {
      return currentSubType.mappingIds.map(attribute => issueCustomAttributesMappings[attribute]);
    }
    return [];
  }

  isFieldMandatory(fieldName) {
    return this.mandatoryFields.includes(fieldName);
  }

  getCustomAttributeFieldKey = attrId => `${TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.CUSTOM_ATTRIBUTES}['${attrId}']`;

  getRequiredErrorMessage = value => !presenceValidator(value) && this.requiredFieldMessage;

  changeFormValue(value, fieldName) {
    const {setErrorsFormAction, changeFormValueAction} = this.props;
    const touched = getTemplateInputValidationMessage(fieldName, value);
    const isRequired = this.isFieldMandatory(fieldName);
    changeFormValueAction(`pendingTemplate['${fieldName}']`, value);
    let submitFailed;
    if (isRequired) {
      submitFailed = this.getRequiredErrorMessage(value);
    }
    setErrorsFormAction(`pendingTemplate['${fieldName}']`, {touched, submitFailed});
  }

  handleCustomAttributeFieldChange = (value, fieldId, isRequired) => {
    const {issueCustomAttributes, changeFormValueAction, setErrorsFormAction} = this.props;
    const customAttribute = issueCustomAttributes[fieldId];
    const touched = getIssueCustomAttributeInputValidationMessage(customAttribute.dataType, value);
    const key = this.getCustomAttributeFieldKey(fieldId);

    let valueToUse = value;
    if (customAttribute.dataType === ISSUE_ATTRIBUTES_DATA_TYPES.NUMERIC) {
      const parsedValue = safeParseNumber(value);
      valueToUse = parsedValue === null ? value : parsedValue;
    }

    changeFormValueAction(`pendingTemplate.${key}`, valueToUse);
    let submitFailed;
    if (isRequired) {
      submitFailed = this.getRequiredErrorMessage(value);
    }
    setErrorsFormAction(`pendingTemplate.${key}`, {touched, submitFailed});
  };

  initFormValues() {
    const {
      localStorageKey,
      pendingTemplate,
      initialValues,
      fieldIssueTypeEntitiesMap,
      setErrorsFormAction,
      defaultIssueSubtypeId,
      templateCategoriesEntitiesMap,
      rootCauseEntitiesMap
    } = this.props;

    const initialFormValues = getTemplateValidInitialFieldValues(
      initialValues,
      pendingTemplate,
      localStorageKey,
      fieldIssueTypeEntitiesMap,
      defaultIssueSubtypeId,
      templateCategoriesEntitiesMap,
      rootCauseEntitiesMap
    );

    Object.entries(initialFormValues)
      .filter(([fieldName, value]) => pendingTemplate[fieldName] !== value)
      .forEach(([fieldName, value]) => this.changeFormValue(value, fieldName));

    const customAttributeMappings = this.getSubTypeCustomAttributeMappings(
      initialFormValues[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.SUBTYPE_ID] ||
        pendingTemplate[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.SUBTYPE_ID]
    );

    this.mandatoryFields.forEach(fieldName => {
      const fieldValue = initialFormValues.hasOwnProperty(fieldName)
        ? initialFormValues[fieldName]
        : pendingTemplate[fieldName];
      setErrorsFormAction(`pendingTemplate.${fieldName}`, {
        submitFailed: this.getRequiredErrorMessage(fieldValue)
      });
    });

    this.getRequiredCustomAttributeIds(customAttributeMappings).forEach(attrId => {
      const key = this.getCustomAttributeFieldKey(attrId);

      setErrorsFormAction(`pendingTemplate.${key}`, {
        submitFailed: this.getRequiredErrorMessage(lodashGet(initialFormValues, key, lodashGet(pendingTemplate, key)))
      });
    });

    this.setState({customAttributeMappings});
  }

  // This method is used by the modal rendering this form
  scrollToTop = () => {
    if (this.scrollableElementRef.current) {
      this.scrollableElementRef.current.scrollTop = 0;
    }
  };

  // This method is used by the modal rendering this form
  scrollToFirstError = () => {
    const allFormFieldNames = this.formFieldNamesOrder
      .filter(f => f !== CREATE_FORM_SEPARATOR)
      .concat(
        this.getRequiredCustomAttributeIds(this.state.customAttributeMappings).map(this.getCustomAttributeFieldKey)
      );

    const firstErrorFieldName = allFormFieldNames.find(this.getFieldErrorMessage);
    if (firstErrorFieldName) {
      lodashGet(this.fieldComponentRefs, firstErrorFieldName).current.scrollIntoView();
    }
  };

  getFieldRef = fieldName => {
    const ref = lodashGet(this.fieldComponentRefs, fieldName);
    if (!ref) {
      const newRef = React.createRef();
      lodashSet(this.fieldComponentRefs, fieldName, newRef);
      return newRef;
    }
    return ref;
  };

  handleSubmit = e => {
    // Handle submit is done using parent's modal
    e.preventDefault();
    // this.props.onSubmit();
  };

  getFieldErrorMessage = fieldName =>
    lodashGet(this.props.pendingTemplateFormAttributes, `${fieldName}.errors.touched`) ||
    lodashGet(this.props.pendingTemplateFormAttributes, `${fieldName}.errors.submitFailed`) ||
    undefined;

  shouldShowFieldError(fieldName) {
    // SubmitFailed - refers to errors that should be shown only after failing to submit form (missing required fields for example).
    // Touched - refers to a field which the user inserted value with errors (for example - too long description).
    const submitFailedCheck =
      this.props.pendingTemplateForm.submitFailed &&
      lodashGet(this.props.pendingTemplateFormAttributes, `${fieldName}.errors.submitFailed`);
    const touchedCheck = lodashGet(this.props.pendingTemplateFormAttributes, `${fieldName}.errors.touched`);
    return !!submitFailedCheck || !!touchedCheck;
  }

  getFormFieldProps(fieldName) {
    return {
      key: fieldName,
      errorMessageText: this.getFieldErrorMessage(fieldName),
      showFieldError: this.shouldShowFieldError(fieldName),
      isFieldMandatory: this.isFieldMandatory(fieldName),
      fieldRef: this.getFieldRef(fieldName)
    };
  }

  handleCategoryChange = category =>
    this.changeFormValue(category ? category.id : null, TEMPLATE_FIELD_NAMES.CATEGORY_ID);

  handleIsActiveChange = isActive => this.changeFormValue(isActive, TEMPLATE_FIELD_NAMES.IS_ACTIVE);

  handleTitleChange = title => this.changeFormValue(title, TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.TITLE);

  handleSubtypeChange = subtype => {
    const {setErrorsFormAction, pendingTemplate} = this.props;
    const prevRequiredCustomAttributes = this.getRequiredCustomAttributeIds(this.state.customAttributeMappings);

    const newCustomAttributeMappings = this.getSubTypeCustomAttributeMappings(subtype.id);
    const newRequiredCustomAttributeIds = this.getRequiredCustomAttributeIds(newCustomAttributeMappings);

    const noLongerRequiredCustomAttributes = prevRequiredCustomAttributes.filter(
      attrId => !newRequiredCustomAttributeIds.includes(attrId)
    );
    const newRequiredCustomAttributes = newRequiredCustomAttributeIds.filter(
      attrId => !prevRequiredCustomAttributes.includes(attrId)
    );
    noLongerRequiredCustomAttributes.forEach(attrId => {
      const key = this.getCustomAttributeFieldKey(attrId);
      setErrorsFormAction(`pendingTemplate.${key}]`, {submitFailed: null});
    });
    newRequiredCustomAttributes.forEach(attrId => {
      const key = this.getCustomAttributeFieldKey(attrId);
      setErrorsFormAction(`pendingTemplate.${key}`, {
        submitFailed: this.getRequiredErrorMessage(lodashGet(pendingTemplate, key))
      });
    });
    this.changeFormValue(subtype.id, TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.SUBTYPE_ID);

    this.setState({
      customAttributeMappings: newCustomAttributeMappings
    });
  };

  handleStatusChange = status => this.changeFormValue(status.status, TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.STATUS);

  handleAssignedToChange = entity => {
    this.changeFormValue(entity && entity.oxygenId, TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.ASSIGNED_TO);
    this.changeFormValue(entity && entity.oxygenType, TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.ASSIGNED_TO_TYPE);
  };

  handleDueDateChange = dueDate => {
    const parsedValue = safeParseInteger(dueDate);
    // Use original value if dueDate isn't an integer - to show the user the original input text.
    const newValue = parsedValue === null ? dueDate : parsedValue;
    this.changeFormValue(newValue, TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.DUE_DATE);
  };

  handleLocationChange = location =>
    this.changeFormValue(location ? location.id : null, TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.LOCATION_ID);

  handleLocationDetailsChange = locationDetails =>
    this.changeFormValue(locationDetails, TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.LOCATION_DETAILS);

  handleOwnerChange = owner => this.changeFormValue(owner.oxygenId, TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.OWNER_ID);

  handleRootCauseChange = rootCause =>
    this.changeFormValue(rootCause && rootCause.id, TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.ROOT_CAUSE_ID);

  handleDescriptionChange = description =>
    this.changeFormValue(description, TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.DESCRIPTION);

  showNewCategoryDropdown = () => {
    this.setState({isNewCategoryDropdown: true});
    this.props.onPopupOpen();
  };

  hideNewCategoryDropdown = () => {
    this.setState({isNewCategoryDropdown: false});
    this.props.onPopupClose();
  };

  onCategoryCreateSuccess = category => {
    this.handleCategoryChange(category);
  };

  renderCreateCategoryPopup() {
    const {isNewCategoryDropdown} = this.state;
    return (
      <AnimateHeightPopUp isOpen={isNewCategoryDropdown}>
        <TemplateCategoryCreatePopupContainer
          isOpen={isNewCategoryDropdown}
          closePopup={this.hideNewCategoryDropdown}
          onCreateSuccess={this.onCategoryCreateSuccess}
          container={this}
        />
      </AnimateHeightPopUp>
    );
  }

  renderCategoryField() {
    const {pendingTemplate, disabled} = this.props;
    const fieldName = TEMPLATE_FIELD_NAMES.CATEGORY_ID;
    const textLabelCategory = i18n.t('createTemplate.category');
    const baseProps = this.getFormFieldProps(fieldName);

    const createNewButton = (
      <Button
        styleType={Button.StyleType.INLINE_LINK}
        onClick={this.showNewCategoryDropdown}
        className="TemplateEditForm__create-category-button"
        data-testid="TemplateEditForm__create-category-button"
      >
        {i18n.t('createTemplate.createNewCategory')}
      </Button>
    );
    return (
      <FormField {...baseProps} labelText={textLabelCategory} actionButton={createNewButton}>
        <TemplateCategorySelectBoxContainer
          container={this.scrollableElementRef.current}
          selectedOptionId={pendingTemplate[fieldName]}
          invalid={baseProps.showFieldError}
          disabled={disabled}
          onChange={this.handleCategoryChange}
          onCreateNewClicked={this.showNewCategoryDropdown}
        />
      </FormField>
    );
  }

  renderIsActiveField() {
    const {pendingTemplate, disabled, canToggleIsActive} = this.props;
    const fieldName = TEMPLATE_FIELD_NAMES.IS_ACTIVE;
    const textLabelIsActive = i18n.t('createTemplate.active');
    const activeTemplateDescription = i18n.t('createTemplate.activeDescription');
    const baseProps = this.getFormFieldProps(fieldName);

    return (
      <FormField {...baseProps} labelText={textLabelIsActive}>
        <div className="TemplateEditForm__is-active-field">
          <span className="TemplateEditForm__is-active-text">{activeTemplateDescription}</span>
          <Toggle
            checked={pendingTemplate[fieldName]}
            onChange={this.handleIsActiveChange}
            disabled={disabled || !canToggleIsActive}
          />
        </div>
      </FormField>
    );
  }

  renderTitleField() {
    const {pendingTemplate, disabled, focusTitle} = this.props;
    const fieldName = TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.TITLE;
    const baseProps = this.getFormFieldProps(fieldName);

    return (
      <FormField {...baseProps} labelText={i18n.t('templateAttributes.title.label')}>
        <TextFieldCore
          className="TemplateEditForm__title-field"
          valid={!baseProps.showFieldError}
          value={pendingTemplate[fieldName]}
          onChange={this.handleTitleChange}
          disabled={disabled}
          autofocus={focusTitle}
        />
      </FormField>
    );
  }

  renderSubtypeField() {
    const {pendingTemplate, disabled} = this.props;

    const fieldName = TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.SUBTYPE_ID;
    const textLabelType = i18n.t('templateAttributes.issueTypeAndSubtype.label');
    const baseProps = this.getFormFieldProps(fieldName);

    return (
      <FormField {...baseProps} labelText={textLabelType}>
        <TemplateIssueTypeSelectDropdownScrollableContainer
          disabled={disabled}
          selectedOptionId={pendingTemplate[fieldName]}
          onChange={this.handleSubtypeChange}
          dropdownMaxWidth={this.dropdownMaxWidth}
          valid={!baseProps.showFieldError}
        />
      </FormField>
    );
  }

  renderStatusField() {
    const {pendingTemplate, disabled} = this.props;
    const fieldName = TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.STATUS;

    const textLabelStatus = i18n.t('templateAttributes.status.label');
    const baseProps = this.getFormFieldProps(fieldName);

    return (
      <FormField {...baseProps} labelText={textLabelStatus}>
        <SelectBoxAdapter
          selectedOptions={this.statusOptions.find(option => option.status === pendingTemplate[fieldName])}
          options={this.statusOptions}
          valid={!baseProps.showFieldError}
          optionRenderer={(value, label) => (
            <StatusColorIndicatorOption statusColor={statusColorMapping[value]} label={label} />
          )}
          valueRenderer={(value, label) => (
            <StatusColorIndicatorOption disabled={disabled} statusColor={statusColorMapping[value]} label={label} />
          )}
          disabled={disabled}
          onChange={this.handleStatusChange}
        />
      </FormField>
    );
  }

  renderAssignedToField() {
    const {pendingTemplate, disabled, groupedProjectEntities} = this.props;

    const fieldName = TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.ASSIGNED_TO;
    const labelText = i18n.t('new.assignedTo');
    const placeholder = i18n.t('new.placeholder.assignedTo');
    const baseProps = this.getFormFieldProps(fieldName);
    const assignToFlatOptions = groupedProjectEntities.reduce((acc, item) => [...acc, ...item[1]], []);

    return (
      <FormField {...baseProps} labelText={labelText}>
        <SelectBoxAdapter
          options={groupedProjectEntities}
          selectedOptions={assignToFlatOptions.find(option => option.id === pendingTemplate[fieldName])}
          grouped
          valid={!baseProps.showFieldError}
          labelKey="name"
          valueKey="oxygenId"
          dropdownMaxWidth={this.dropdownMaxWidth}
          subtextGetter={getUserCompanyName}
          placeholder={placeholder}
          disabled={disabled}
          onChange={this.handleAssignedToChange}
          searchable
          searchIcon
          searchPlaceholder={i18n.t('new.search')}
          clearable
        />
      </FormField>
    );
  }

  renderDueDateField() {
    const {pendingTemplate, disabled} = this.props;
    const fieldName = TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.DUE_DATE;
    const labelText = i18n.t('new.dueDate');
    const dueDateDescription = i18n.t('createTemplate.dueDateDescription');
    const baseProps = this.getFormFieldProps(fieldName);
    const value = pendingTemplate[fieldName];

    return (
      <FormField {...baseProps} labelText={labelText}>
        <div className="TemplateEditForm__due-date-container">
          <TextFieldCore
            valid={!baseProps.showFieldError}
            value={isNullOrUndefined(value) ? value : value.toString()}
            onChange={this.handleDueDateChange}
            disabled={disabled}
          />
          <span className="TemplateEditForm__due-date-text">{dueDateDescription}</span>
        </div>
      </FormField>
    );
  }

  renderLocationField() {
    const {pendingTemplate, disabled} = this.props;
    const fieldName = TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.LOCATION_ID;
    const labelText = i18n.t('new.location');
    const baseProps = this.getFormFieldProps(fieldName);

    return (
      <FormField {...baseProps} labelText={labelText}>
        <IssueLocationPicker
          selectedEventKey={pendingTemplate[fieldName]}
          valid={!baseProps.showFieldError}
          disabled={disabled}
          onChange={this.handleLocationChange}
        />
      </FormField>
    );
  }

  renderLocationDetailsField() {
    const {pendingTemplate, disabled} = this.props;
    const fieldName = TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.LOCATION_DETAILS;
    const textLabelTitle = i18n.t('new.locationDetails');
    const baseProps = this.getFormFieldProps(fieldName);

    return (
      <FormField {...baseProps} labelText={textLabelTitle}>
        <TextFieldCore
          valid={!baseProps.showFieldError}
          value={pendingTemplate[fieldName]}
          onChange={this.handleLocationDetailsChange}
          disabled={disabled}
        />
      </FormField>
    );
  }

  renderOwnerField() {
    const {pendingTemplate, disabled, users} = this.props;

    const fieldName = TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.OWNER_ID;
    const labelText = i18n.t('new.owner');
    const placeholder = i18n.t('new.placeholder.owner');
    const baseProps = this.getFormFieldProps(fieldName);

    return (
      <FormField {...baseProps} labelText={labelText}>
        <SelectBoxAdapter
          options={users}
          selectedOptions={users.find(user => user.oxygenId === pendingTemplate[fieldName])}
          valid={!baseProps.showFieldError}
          labelKey="name"
          valueKey="oxygenId"
          dropdownMaxWidth={this.dropdownMaxWidth}
          subtextGetter={getUserCompanyName}
          placeholder={placeholder}
          disabled={disabled}
          onChange={this.handleOwnerChange}
          searchable
          searchIcon
          searchPlaceholder={i18n.t('new.search')}
        />
      </FormField>
    );
  }

  renderRootCauseField() {
    const {disabled, pendingTemplate} = this.props;
    const fieldName = TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.ROOT_CAUSE_ID;

    const textLabelRootCause = i18n.t('new.rootCause');
    const baseProps = this.getFormFieldProps(fieldName);

    return (
      <FormField {...baseProps} labelText={textLabelRootCause}>
        <TemplateIssueRootCauseSelectDropdownWithScrollableContainer
          selectedOptionId={pendingTemplate[fieldName]}
          dropdownMaxWidth={this.dropdownMaxWidth}
          valid={!baseProps.showFieldError}
          disabled={disabled}
          onChange={this.handleRootCauseChange}
        />
      </FormField>
    );
  }

  renderDescriptionField() {
    const {pendingTemplate, disabled} = this.props;
    const fieldName = TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.DESCRIPTION;
    const textLabel = i18n.t('new.description');
    const baseProps = this.getFormFieldProps(fieldName);

    return (
      <FormField {...baseProps} labelText={textLabel}>
        <TextFieldCore
          behavior={TextInputBehaviors.MULTI_LINE}
          value={pendingTemplate[fieldName]}
          valid={!baseProps.showFieldError}
          onChange={this.handleDescriptionChange}
          disabled={disabled}
          className="TemplateEditForm__multi-line-text-field"
        />
      </FormField>
    );
  }

  renderCustomAttributesFields() {
    const {pendingTemplate, disabled, issueCustomAttributes} = this.props;
    const {customAttributeMappings} = this.state;
    if (customAttributeMappings.length) {
      return (
        <React.Fragment key={TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.CUSTOM_ATTRIBUTES}>
          {this.renderSeparator()}
          {customAttributeMappings.map(mapping => {
            const isRequired = mapping.isRequired;
            const customAttribute = issueCustomAttributes[mapping.attributeDefinitionId];
            const formFieldKey = this.getCustomAttributeFieldKey(customAttribute.id);
            const pendingCustomAttribute = lodashGet(pendingTemplate, formFieldKey);
            const showFieldError = this.shouldShowFieldError(formFieldKey);
            const errorMessage = this.getFieldErrorMessage(formFieldKey);
            return (
              <IssueCustomAttributeField
                key={customAttribute.id}
                fieldRef={this.getFieldRef(formFieldKey)}
                value={pendingCustomAttribute}
                disabled={disabled}
                onChange={(value, fieldId) => this.handleCustomAttributeFieldChange(value, fieldId, isRequired)}
                showFieldError={showFieldError}
                errorMessage={errorMessage}
                customAttribute={customAttribute}
                isRequired={isRequired}
                container={this.scrollableElementRef.current}
              />
            );
          })}
        </React.Fragment>
      );
    }
    return null;
  }

  renderSeparator(key) {
    return <div key={key} className="TemplateEditForm__separator" />;
  }

  renderField = (fieldName, index) => {
    switch (fieldName) {
      case CREATE_FORM_SEPARATOR:
        return this.renderSeparator(index);
      case TEMPLATE_FIELD_NAMES.CATEGORY_ID:
        return this.renderCategoryField();
      case TEMPLATE_FIELD_NAMES.IS_ACTIVE:
        return this.renderIsActiveField();
      case TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.TITLE:
        return this.renderTitleField();
      case TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.SUBTYPE_ID:
        return this.renderSubtypeField();
      case TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.STATUS:
        return this.renderStatusField();
      case TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.ASSIGNED_TO:
        return this.renderAssignedToField();
      case TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.DUE_DATE:
        return this.renderDueDateField();
      case TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.LOCATION_ID:
        return this.renderLocationField();
      case TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.LOCATION_DETAILS:
        return this.renderLocationDetailsField();
      case TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.OWNER_ID:
        return this.renderOwnerField();
      case TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.ROOT_CAUSE_ID:
        return this.renderRootCauseField();
      case TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.DESCRIPTION:
        return this.renderDescriptionField();
      case TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.CUSTOM_ATTRIBUTES:
        return this.renderCustomAttributesFields();
      default:
        return null;
    }
  };

  render() {
    const {isNewCategoryDropdown} = this.state;
    const formContentClasses = cx('TemplateEditForm__form-content', {
      'TemplateEditForm__form-content--shadowed': isNewCategoryDropdown
    });
    return (
      <form onSubmit={this.handleSubmit} className="TemplateEditForm">
        {this.renderCreateCategoryPopup()}
        <div className={formContentClasses} ref={this.scrollableElementRef}>
          {this.formFieldNamesOrder.map(this.renderField)}
        </div>
      </form>
    );
  }
}

TemplateEditForm.displayName = 'TemplateEditForm';

TemplateEditForm.propTypes = {
  pendingTemplate: PropTypes.object.isRequired,
  pendingTemplateForm: PropTypes.object.isRequired,
  pendingTemplateFormAttributes: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onPopupOpen: PropTypes.func.isRequired,
  onPopupClose: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  container: PropTypes.object.isRequired,
  localStorageKey: PropTypes.string,
  initialValues: PropTypes.object,
  issueCustomAttributesMappings: PropTypes.object.isRequired,
  issueCustomAttributes: PropTypes.object.isRequired,
  fieldIssueTypeEntitiesMap: PropTypes.object.isRequired,
  rootCauseEntitiesMap: PropTypes.object.isRequired,
  users: PropTypes.array.isRequired,
  groupedProjectEntities: PropTypes.array.isRequired,
  changeFormValueAction: PropTypes.func.isRequired,
  setErrorsFormAction: PropTypes.func.isRequired,
  focusTitle: PropTypes.bool,
  canToggleIsActive: PropTypes.bool,
  defaultIssueSubtypeId: PropTypes.string,
  templateCategoriesEntitiesMap: templateCategoriesEntitiesMapType
};

export default TemplateEditForm;
